// Mobile "drill-down" menu drawer (WAI-ARIA APG modal dialog pattern).
//
// Unlike the desktop mega menu (megaMenu.ts) — a non-modal disclosure where
// flyouts share the page with normal Tab flow and never trap focus — this
// drawer sits behind a dimming, pointer-blocking .backdrop once open, which
// makes it functionally a modal. WAI-ARIA APG's guidance for that case is to
// move focus into the drawer on open, and trap Tab/Shift+Tab so they cycle
// only through the drawer's own content instead of escaping into the dimmed
// page behind it.
//
// Root and panels are "screens" stacked in the same CSS grid cell (see
// .mobile-menu-screen in app.css: grid-area: 1 / 1), so .mobile-menu-screens
// auto-sizes to whichever currently-visible screen is tallest — including
// staying tall enough for both while a panel is mid-slide over the root —
// with no JS height measurement needed. Each screen has no internal
// scrollbar; the surrounding drawer (overflow-y-auto in nav.php) scrolls as
// a whole if that's taller than the viewport.
//
// The back button and heading live inside each panel itself (rendered
// per-panel by PHP, so the title never needs a JS text swap) rather than in
// <header> — the header only ever shows the logo and the toggle button.
//
// While a screen is covered it's still in the DOM (needed so it's visible
// mid-transition) but marked `inert`, which removes it from the
// accessibility tree, Tab order, and pointer interaction — removed again
// only once the transition finishes, so focus never lands on something
// that's still visually obscured. Marking a panel inert on close also
// covers its own back button/heading automatically, since they're inside it.

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const PANEL_TRANSITION_MS = 300

export default function initMobileMenu() {
  const toggleButton = document.querySelector('.mobile-menu-button') as HTMLButtonElement | null
  const backdrop = document.querySelector('.backdrop') as HTMLElement | null
  const menuHolder = document.querySelector('.menu-holder') as HTMLElement | null
  const header = document.querySelector('header') as HTMLElement | null

  if (!toggleButton || !backdrop || !menuHolder || !header) {
    return
  }

  const root = document.getElementById('mobile-menu-root') as HTMLElement | null
  const panels = Array.from(menuHolder.querySelectorAll('.mobile-menu-panel')) as HTMLElement[]
  const itemTriggers = Array.from(
    menuHolder.querySelectorAll('.mobile-menu-item-trigger')
  ) as HTMLButtonElement[]

  const desktopBreakpoint = window.matchMedia('(min-width: 1280px)')
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

  let isOpen = false
  let isAnimating = false
  let currentPanel: HTMLElement | null = null

  // --- screens: root list vs. a drilled-into submenu ------------------------

  const getPanel = (id: string) => panels.find((panel) => panel.id === id) ?? null

  const setChromeForRoot = () => {
    itemTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'))
  }

  const setChromeForPanel = (trigger: HTMLButtonElement) => {
    itemTriggers.forEach((t) => t.setAttribute('aria-expanded', String(t === trigger)))
  }

  // Runs `callback` once the element's transform transition finishes, with a
  // timeout fallback in case the event never fires (e.g. the element gets
  // hidden some other way). Reduced-motion skips straight to the callback,
  // since there's no transition to wait for.
  const afterTransition = (el: HTMLElement, callback: () => void) => {
    if (reducedMotion.matches) {
      callback()
      return
    }
    let done = false
    const finish = () => {
      if (done) {
        return
      }
      done = true
      el.removeEventListener('transitionend', onTransitionEnd)
      window.clearTimeout(fallback)
      callback()
    }
    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target === el && event.propertyName === 'transform') {
        finish()
      }
    }
    el.addEventListener('transitionend', onTransitionEnd)
    const fallback = window.setTimeout(finish, PANEL_TRANSITION_MS + 50)
  }

  // Instant reset used when the whole drawer closes — no slide, since the
  // backdrop/drawer fade already covers that transition.
  const resetToRootInstant = () => {
    panels.forEach((panel) => {
      panel.hidden = true
      panel.classList.remove('is-off-right')
      panel.removeAttribute('inert')
    })
    if (root) {
      root.removeAttribute('inert')
    }
    currentPanel = null
    isAnimating = false
    setChromeForRoot()
  }

  const openPanel = (panel: HTMLElement, trigger: HTMLButtonElement) => {
    if (isAnimating || panel === currentPanel) {
      return
    }
    isAnimating = true
    currentPanel = panel

    setChromeForPanel(trigger)
    if (root) {
      root.setAttribute('inert', '')
    }

    panel.hidden = false
    panel.classList.add('is-off-right')
    // Force layout so the browser registers the off-right position first —
    // otherwise removing the class next tick would just apply the end state
    // with nothing to transition from.
    void panel.getBoundingClientRect()

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.classList.remove('is-off-right')
      })
    })

    afterTransition(panel, () => {
      isAnimating = false
      // Move focus to the new screen's own heading (tabindex="-1": focusable
      // programmatically but not part of the natural Tab order) only once
      // the panel has actually finished sliding into place — focusing it
      // earlier, while it's still transform-offset off-screen, makes the
      // browser scroll-into-view mid-slide, which is the "bouncy" jump.
      panel.querySelector<HTMLElement>('.mobile-menu-panel-heading')?.focus()
    })
  }

  const closeCurrentPanel = (focusTarget: HTMLElement | null) => {
    const panel = currentPanel
    if (isAnimating || !panel) {
      return
    }
    isAnimating = true

    setChromeForRoot()
    panel.setAttribute('inert', '')
    panel.classList.add('is-off-right')

    afterTransition(panel, () => {
      panel.hidden = true
      panel.classList.remove('is-off-right')
      panel.removeAttribute('inert')
      if (root) {
        root.removeAttribute('inert')
      }
      currentPanel = null
      isAnimating = false
      // Focus only once the panel has actually finished sliding away and
      // root is no longer inert — focusing any earlier would silently fail.
      focusTarget?.focus()
    })
  }

  itemTriggers.forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const targetId = trigger.dataset.mobileMenuTarget
      const panel = targetId ? getPanel(targetId) : null
      if (!panel) {
        return
      }
      openPanel(panel, trigger)
    })
  })

  // Delegated: each panel renders its own back button, so there's one per
  // panel rather than a single shared element. Only the current (non-inert)
  // panel's button can ever actually be clicked, so no extra lookup needed.
  menuHolder.addEventListener('click', (event) => {
    const backButton = (event.target as HTMLElement).closest('.mobile-menu-back-button')
    if (!backButton) {
      return
    }
    const previousTrigger = itemTriggers.find((t) => t.getAttribute('aria-expanded') === 'true')
    // Return focus to the trigger just drilled into, not the top of the list.
    closeCurrentPanel(previousTrigger ?? itemTriggers[0] ?? null)
  })

  // --- open / close -----------------------------------------------------------

  const getDrawerFocusable = () =>
    (Array.from(menuHolder.querySelectorAll(FOCUSABLE_SELECTOR)) as HTMLElement[]).filter(
      (el) => el.offsetParent !== null && !el.closest('[inert]')
    )

  const open = () => {
    if (isOpen) {
      return
    }
    isOpen = true

    toggleButton.setAttribute('aria-expanded', 'true')
    backdrop.classList.add('is-active')
    menuHolder.style.display = 'block'
    window.setTimeout(() => {
      menuHolder.style.opacity = '1'
    }, 100)

    document.addEventListener('keydown', onKeydown)
    document.addEventListener('click', onDocumentClick)

    // WAI-ARIA APG dialog pattern: move focus inside on open. The drawer
    // always opens on the root screen, so its first parent-item trigger is
    // the natural first stop; falls back to the first focusable element
    // (e.g. "Join us") if menu_header has no rows yet.
    ;(itemTriggers[0] ?? getDrawerFocusable()[0])?.focus()
  }

  const close = () => {
    if (!isOpen) {
      return
    }
    isOpen = false

    toggleButton.setAttribute('aria-expanded', 'false')
    backdrop.classList.remove('is-active')
    menuHolder.style.opacity = '0'
    window.setTimeout(() => {
      menuHolder.style.display = 'none'
    }, 300)

    document.removeEventListener('keydown', onKeydown)
    document.removeEventListener('click', onDocumentClick)

    // Always reset to the root screen, so a future open() never starts
    // mid-submenu.
    resetToRootInstant()
    toggleButton.focus()
  }

  toggleButton.addEventListener('click', () => {
    if (isOpen) {
      close()
    } else {
      open()
    }
  })

  // --- focus trap ---------------------------------------------------------------
  //
  // The header now only ever shows the logo and the toggle button (both
  // always visible, never hidden while the drawer is open), so header
  // focusables are simple to include. Everything else the trap needs — the
  // root list, each panel's own back button/heading, its links, its CTA —
  // lives inside .menu-holder. Concatenated header-first, since <header>
  // precedes .menu-holder in the markup, reproducing the tab order a
  // sighted keyboard user would expect.
  //
  // Rebuilt on every Tab press (not cached), since which elements are
  // visible changes as the user drills in/out. Visibility is checked via
  // `offsetParent !== null`, which excludes anything hidden either via the
  // native `hidden` attribute or a Tailwind display utility (e.g. the
  // desktop mega menu's own `hidden xl:flex` nav, which also lives inside
  // <header> and must never be reachable by this trap at mobile widths).
  // The covered screen (root or a panel mid-transition) is excluded too via
  // `[inert]`, since it stays un-hidden during the slide but must not be
  // reachable — that also covers a closing panel's own back button/heading.
  const getTrapFocusable = (): HTMLElement[] => {
    const headerFocusable = Array.from(header.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    return [...headerFocusable, ...getDrawerFocusable()].filter(
      (el) => el.offsetParent !== null && !el.closest('[inert]')
    )
  }

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close()
      return
    }

    if (event.key !== 'Tab') {
      return
    }

    const focusable = getTrapFocusable()
    if (focusable.length === 0) {
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement as HTMLElement | null

    if (event.shiftKey && active === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  // --- backdrop click closes -----------------------------------------------------

  const onDocumentClick = (event: MouseEvent) => {
    if (event.target === backdrop) {
      close()
    }
  }

  // --- auto-close once the toggle button itself is hidden (>= xl) ----------------

  desktopBreakpoint.addEventListener('change', (event) => {
    if (event.matches) {
      close()
    }
  })
}
